import {
  Alert,
  Dialog,
  DialogActionFooter,
  DialogCloseButton,
  DialogCloseIcon,
  DialogContent,
  DialogDescription,
  DialogEmphasis,
  DialogHeader,
  DialogTitle,
} from 'components/ui';
import {
  firmDocumentDialogErrorAlertSx,
  FirmDocumentMoveDialogTarget,
} from './FirmDocumentFileActionDialogs.styles';
import { FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS } from './FirmDocumentFileActionDialogs.testIds';
import type { FirmDocumentMoveDialogProps } from './FirmDocumentMoveDialog.props';

const renderTargetLabel = (targetLabel?: string | null) => {
  if (!targetLabel) {
    return 'the selected destination';
  }

  const [companyName, ...folderParts] = targetLabel.split(' > ');
  const folderName = folderParts.join(' > ');

  if (!folderName) {
    return targetLabel;
  }

  return (
    <FirmDocumentMoveDialogTarget>
      <span>{companyName}</span>
      <span data-firm-document-move-dialog-target-separator="true">&gt;</span>
      <span>{folderName}</span>
    </FirmDocumentMoveDialogTarget>
  );
};

const FirmDocumentMoveDialog = ({
  errorMessage = null,
  fileName,
  isMoving = false,
  onClose,
  onConfirm,
  open,
  sourceLabel,
  targetLabel,
}: FirmDocumentMoveDialogProps) => (
  <Dialog
    data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.moveDialog}
    maxWidth="xs"
    onClose={isMoving ? undefined : onClose}
    open={open}>
    <DialogHeader>
      <DialogTitle>Move Document</DialogTitle>
      <DialogCloseButton
        aria-label="Close move document dialog"
        data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.moveCloseButton}
        disabled={isMoving}
        onClick={onClose}
        size="small">
        <DialogCloseIcon />
      </DialogCloseButton>
    </DialogHeader>
    <DialogContent>
      <DialogDescription>
        <span>
          Move <DialogEmphasis>{fileName ?? 'this document'}</DialogEmphasis>
          {sourceLabel ? <> from <DialogEmphasis>{sourceLabel}</DialogEmphasis></> : null}
          {' '}to <DialogEmphasis>{renderTargetLabel(targetLabel)}</DialogEmphasis>?
        </span>
      </DialogDescription>
      {errorMessage ? (
        <Alert
          data-testid={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.moveErrorMessage}
          severity="error"
          sx={firmDocumentDialogErrorAlertSx}
          visualVariant="inline">
          {errorMessage}
        </Alert>
      ) : null}
    </DialogContent>
    <DialogActionFooter
      actionButtonTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.moveConfirmButton}
      actionLabel="Move Document"
      cancelButtonTestId={FIRM_DOCUMENT_FILE_ACTION_DIALOG_TEST_IDS.moveCancelButton}
      isActionLoading={isMoving}
      isCancelDisabled={isMoving}
      onAction={onConfirm}
      onCancel={onClose}
    />
  </Dialog>
);

export default FirmDocumentMoveDialog;
